import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { convertCurrency } from '@/lib/api';
import CurrencyMiniChart from '@/components/CurrencyMiniChart';

const POPULAR = ['USD','EUR','GBP','JPY','INR','AUD','CAD','CHF','CNY','HKD','SGD','KRW','THB','AED','MXN','BRL','TRY','ZAR','IDR','MYR','PHP','VND','NZD','SEK','NOK','DKK'];

export default function CurrencyCard({ defaultFrom = 'USD', defaultTo = 'EUR', rates }) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [amount, setAmount] = useState(100);
  const [converted, setConverted] = useState(null);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setFrom(defaultFrom); }, [defaultFrom]);
  useEffect(() => { if (defaultTo && defaultTo !== from) setTo(defaultTo); }, [defaultTo]);

  const currencies = useMemo(() => {
    const set = new Set(POPULAR);
    if (rates) Object.keys(rates).forEach((k) => set.add(k));
    return Array.from(set).sort();
  }, [rates]);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!from || !to || !amount) return;
      setLoading(true);
      try {
        const r = await convertCurrency(from, to, Number(amount));
        if (!cancel) { setConverted(r.converted); setRate(r.rate); }
      } catch { if (!cancel) { setConverted(null); setRate(null); } }
      finally { if (!cancel) setLoading(false); }
    };
    const t = setTimeout(run, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [from, to, amount]);

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div className="puvio-card p-5" data-testid="currency-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Currency</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">Live conversion</p>
        </div>
        <button type="button" aria-label="Swap currencies" onClick={swap} className="w-11 h-11 rounded-2xl bg-[var(--puvio-bg-soft)] flex items-center justify-center hover:bg-[var(--puvio-bg-soft-hover)] transition-colors puvio-focus" data-testid="currency-swap">
          <ArrowLeftRight className="w-5 h-5 text-[#0F1A13]" />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-5 gap-2 items-center">
        <div className="col-span-2">
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-12 rounded-2xl border border-[var(--puvio-border)] px-3 text-lg font-extrabold bg-white puvio-focus tabular-nums" data-testid="currency-amount" />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="mt-2 w-full h-10 rounded-2xl border border-[var(--puvio-border)] px-3 bg-white text-sm font-bold puvio-focus" data-testid="currency-from" aria-label="From currency">
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="text-center text-xs text-[var(--puvio-text-2)] font-bold">≈</div>
        <div className="col-span-2">
          <div className="h-12 rounded-2xl bg-[#15291C] text-[#C4FF3D] px-3 flex items-center justify-end text-lg font-extrabold tabular-nums" data-testid="currency-result">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (converted != null ? converted.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—')}
          </div>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="mt-2 w-full h-10 rounded-2xl border border-[var(--puvio-border)] px-3 bg-white text-sm font-bold puvio-focus" data-testid="currency-to" aria-label="To currency">
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {rate != null && (
        <p className="text-[11px] text-[var(--puvio-text-2)] mt-3 font-semibold" data-testid="currency-rate-hint">1 {from} = {rate.toLocaleString(undefined, { maximumFractionDigits: 4 })} {to}</p>
      )}
      <CurrencyMiniChart from={from} to={to} days={30} />
    </div>
  );
}
